"use client";

import type { RefObject } from "react";
import {
  glyphDot,
  glyphPath,
  type PigpenCell,
  type PigpenGlyph,
  pigpenGlyph,
} from "@/lib/tool-kit/ciphers/pigpen";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Drawing for the pigpen cipher: one glyph, a whole message, and the standard key.
 * Colours are attributes, not classes, so a downloaded SVG or PNG looks the same as the
 * page: an exported image carries no stylesheet.
 */

const STROKE = 3;
const DOT_RADIUS = 3.2;

function GlyphShape({
  glyph,
  x,
  y,
  size,
  ink,
}: {
  glyph: PigpenGlyph;
  x: number;
  y: number;
  size: number;
  /** A fixed colour for exported images; `currentColor` follows the page's theme. */
  ink: string;
}) {
  const dot = glyphDot(glyph, x, y, size);
  return (
    <>
      <path
        d={glyphPath(glyph, x, y, size)}
        fill="none"
        stroke={ink}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeWidth={STROKE}
      />
      {dot ? (
        <circle cx={dot.cx} cy={dot.cy} fill={ink} r={DOT_RADIUS} />
      ) : null}
    </>
  );
}

/**
 * One symbol, for buttons and the symbol chart, in the current text colour so it reads
 * in light and dark themes. Decorative: the caller names it.
 */
export function GlyphIcon({
  letter,
  size = 32,
}: {
  letter: string;
  size?: number;
}) {
  const glyph = pigpenGlyph(letter);
  if (!glyph) {
    return null;
  }
  return (
    <svg
      aria-hidden="true"
      className="inline-block"
      height={size}
      viewBox="0 0 32 32"
      width={size}
    >
      <GlyphShape glyph={glyph} ink="currentColor" size={20} x={6} y={6} />
    </svg>
  );
}

/** Layout of a drawn message, in SVG units. */
export const MESSAGE_CELL = 44;
const MESSAGE_GLYPH = 26;
const MESSAGE_MARGIN = 16;

/** The message as one self-contained SVG, ready to download. */
export function PigpenMessageSvg({
  lines,
  perLine,
  label,
  svgRef,
}: {
  lines: readonly (readonly PigpenCell[])[];
  perLine: number;
  label: string;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const width = perLine * MESSAGE_CELL + MESSAGE_MARGIN * 2;
  const height = Math.max(lines.length, 1) * MESSAGE_CELL + MESSAGE_MARGIN * 2;
  const inset = (MESSAGE_CELL - MESSAGE_GLYPH) / 2;
  return (
    <svg
      aria-label={label}
      className="h-auto max-w-full"
      height={height}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill={CHART_INK.background} height={height} width={width} />
      {lines.map((line, row) =>
        line.map((cell, column) => {
          const glyph = cell === null ? null : pigpenGlyph(cell);
          if (!glyph) {
            return null;
          }
          return (
            <GlyphShape
              glyph={glyph}
              ink={CHART_INK.text}
              // biome-ignore lint/suspicious/noArrayIndexKey: symbols are positional
              key={`${row}-${column}`}
              size={MESSAGE_GLYPH}
              x={MESSAGE_MARGIN + column * MESSAGE_CELL + inset}
              y={MESSAGE_MARGIN + row * MESSAGE_CELL + inset}
            />
          );
        })
      )}
    </svg>
  );
}

const KEY_GRID_LETTERS = ["ABCDEFGHI", "JKLMNOPQR"] as const;
const KEY_X_LETTERS = ["STUV", "WXYZ"] as const;
const FIGURE = 120;
const THIRD = FIGURE / 3;
const LETTER_STYLE = {
  fill: CHART_INK.text,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 18,
  fontWeight: 600,
} as const;

function KeyGrid({
  letters,
  dots,
  x,
}: {
  letters: string;
  dots: boolean;
  x: number;
}) {
  const lines = [
    `M${x + THIRD} 0V${FIGURE}`,
    `M${x + 2 * THIRD} 0V${FIGURE}`,
    `M${x} ${THIRD}H${x + FIGURE}`,
    `M${x} ${2 * THIRD}H${x + FIGURE}`,
  ].join("");
  return (
    <g>
      <path
        d={lines}
        fill="none"
        stroke={CHART_INK.text}
        strokeWidth={STROKE}
      />
      {[...letters].map((letter, index) => {
        const cx = x + (index % 3) * THIRD + THIRD / 2;
        const cy = Math.floor(index / 3) * THIRD + THIRD / 2;
        return (
          <g key={letter}>
            <text
              {...LETTER_STYLE}
              dominantBaseline="central"
              textAnchor="middle"
              x={dots ? cx - 6 : cx}
              y={cy}
            >
              {letter}
            </text>
            {dots ? (
              <circle
                cx={cx + 9}
                cy={cy}
                fill={CHART_INK.text}
                r={DOT_RADIUS}
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

/**
 * Letter positions in an X — top, left, right, bottom — as fractions of the figure, and
 * where the letter and its dot sit when the X is the dotted one: the dot goes on the
 * side of the letter that points into the wedge.
 */
const X_SLOTS: readonly {
  at: readonly [number, number];
  shift: readonly [number, number];
  dot: readonly [number, number];
}[] = [
  { at: [0.5, 0.2], shift: [0, -5], dot: [0, 12] },
  { at: [0.2, 0.5], shift: [0, 0], dot: [13, 0] },
  { at: [0.8, 0.5], shift: [0, 0], dot: [-13, 0] },
  { at: [0.5, 0.8], shift: [0, 5], dot: [0, -12] },
];

function KeyX({
  letters,
  dots,
  x,
}: {
  letters: string;
  dots: boolean;
  x: number;
}) {
  const d = `M${x + 10} 10L${x + FIGURE - 10} ${FIGURE - 10}M${x + FIGURE - 10} 10L${x + 10} ${FIGURE - 10}`;
  return (
    <g>
      <path d={d} fill="none" stroke={CHART_INK.text} strokeWidth={STROKE} />
      {[...letters].map((letter, index) => {
        const slot = X_SLOTS[index];
        const cx = x + slot.at[0] * FIGURE;
        const cy = slot.at[1] * FIGURE;
        const [dx, dy] = dots ? slot.shift : [0, 0];
        return (
          <g key={letter}>
            <text
              {...LETTER_STYLE}
              dominantBaseline="central"
              textAnchor="middle"
              x={cx + dx}
              y={cy + dy}
            >
              {letter}
            </text>
            {dots ? (
              <circle
                cx={cx + slot.dot[0]}
                cy={cy + slot.dot[1]}
                fill={CHART_INK.text}
                r={DOT_RADIUS}
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

/** The standard key: two noughts-and-crosses grids and two Xs, dots on the second of each. */
export function PigpenKey({ label }: { label: string }) {
  const gap = 28;
  const width = FIGURE * 4 + gap * 3;
  return (
    <svg
      aria-label={label}
      className="h-auto w-full max-w-xl"
      role="img"
      viewBox={`-4 -4 ${width + 8} ${FIGURE + 8}`}
    >
      <rect
        fill={CHART_INK.background}
        height={FIGURE + 8}
        width={width + 8}
        x={-4}
        y={-4}
      />
      <KeyGrid dots={false} letters={KEY_GRID_LETTERS[0]} x={0} />
      <KeyGrid dots letters={KEY_GRID_LETTERS[1]} x={FIGURE + gap} />
      <KeyX dots={false} letters={KEY_X_LETTERS[0]} x={(FIGURE + gap) * 2} />
      <KeyX dots letters={KEY_X_LETTERS[1]} x={(FIGURE + gap) * 3} />
    </svg>
  );
}

/** Button order for the decoding keyboard: the key's own four figures. */
export const KEY_FIGURES: readonly { letters: string; kind: "grid" | "x" }[] = [
  { letters: KEY_GRID_LETTERS[0], kind: "grid" },
  { letters: KEY_GRID_LETTERS[1], kind: "grid" },
  { letters: KEY_X_LETTERS[0], kind: "x" },
  { letters: KEY_X_LETTERS[1], kind: "x" },
];
